import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import c from 'highlight.js/lib/languages/c'
import cpp from 'highlight.js/lib/languages/cpp'
import csharp from 'highlight.js/lib/languages/csharp'
import css from 'highlight.js/lib/languages/css'
import go from 'highlight.js/lib/languages/go'
import java from 'highlight.js/lib/languages/java'
import javascript from 'highlight.js/lib/languages/javascript'
import json from 'highlight.js/lib/languages/json'
import kotlin from 'highlight.js/lib/languages/kotlin'
import markdown from 'highlight.js/lib/languages/markdown'
import php from 'highlight.js/lib/languages/php'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import rust from 'highlight.js/lib/languages/rust'
import shell from 'highlight.js/lib/languages/shell'
import sql from 'highlight.js/lib/languages/sql'
import swift from 'highlight.js/lib/languages/swift'
import typescript from 'highlight.js/lib/languages/typescript'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'
import type { LanguageFn } from 'highlight.js'

import { maxDebug } from './max-debug'

/**
 * Configuration options for markdown parsing.
 */
export type MarkdownOptions = {
  enableHtml: boolean
  enableBreaks: boolean
  enableTable: boolean
  enableLinkify: boolean
  enableTypography: boolean
  /** Render ```html fences as live HTML instead of a code block. */
  renderHtmlBlocks: boolean
}

export type MarkdownElementNode = {
  kind: 'element'
  tag: string
  attrs: Record<string, string>
  children: MarkdownRenderNode[]
}

export type MarkdownTextNode = {
  kind: 'text'
  value: string
}

export type MarkdownCodeNode = {
  kind: 'code'
  code: string
  language: string
  key: string
}

export type MarkdownHtmlBlockNode = {
  kind: 'html-block'
  source: string
  key: string
}

/** An `html`/`htm` fence whose closing marker hasn't streamed in yet. */
export type MarkdownHtmlBlockPendingNode = {
  kind: 'html-block-pending'
  source: string
  key: string
}

/** Safe, component-ready representation of the markdown token stream. */
export type MarkdownRenderNode =
  | MarkdownElementNode
  | MarkdownTextNode
  | MarkdownCodeNode
  | MarkdownHtmlBlockNode
  | MarkdownHtmlBlockPendingNode

export type CodeBlockPresentation = {
  languageClass: string
  languageLabel: string
  highlightedCode: string
}

/**
 * Default markdown configuration.
 * Raw HTML is disabled; assistant markdown is rendered from tokens rather than injected HTML.
 */
const DEFAULT_OPTIONS: MarkdownOptions = {
  enableHtml: false,
  enableBreaks: true,
  enableTable: true,
  enableLinkify: true,
  enableTypography: true,
  renderHtmlBlocks: true,
}

const RENDERABLE_HTML_LANGUAGES = new Set(['html', 'htm'])

const LANGUAGE_ALIASES: Record<string, string> = {
  'c#': 'csharp',
  'c++': 'cpp',
  cs: 'csharp',
  html: 'xml',
  js: 'javascript',
  md: 'markdown',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  text: 'plaintext',
  ts: 'typescript',
  vue: 'xml',
  yml: 'yaml',
}

const LANGUAGE_LABELS: Record<string, string> = {
  bash: 'Bash',
  c: 'C',
  cpp: 'C++',
  csharp: 'C#',
  css: 'CSS',
  go: 'Go',
  java: 'Java',
  javascript: 'JavaScript',
  json: 'JSON',
  kotlin: 'Kotlin',
  markdown: 'Markdown',
  php: 'PHP',
  plaintext: 'Plain text',
  python: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  sql: 'SQL',
  swift: 'Swift',
  typescript: 'TypeScript',
  xml: 'HTML/XML',
  yaml: 'YAML',
}

const HIGHLIGHT_LANGUAGES: Array<[string, LanguageFn]> = [
  ['bash', bash],
  ['c', c],
  ['cpp', cpp],
  ['csharp', csharp],
  ['css', css],
  ['go', go],
  ['java', java],
  ['javascript', javascript],
  ['json', json],
  ['kotlin', kotlin],
  ['markdown', markdown],
  ['php', php],
  ['python', python],
  ['ruby', ruby],
  ['rust', rust],
  ['shell', shell],
  ['sql', sql],
  ['swift', swift],
  ['typescript', typescript],
  ['xml', xml],
  ['yaml', yaml],
]

const INLINE_CODE_CLASS = 'md-inline-code'

const TOKEN_ATTRIBUTES = new Set(['href', 'title', 'src', 'alt', 'align', 'colspan', 'rowspan'])

let highlightLanguagesRegistered = false
let mdInstance: MarkdownIt | null = null

const registerHighlightLanguages = (): void => {
  if (highlightLanguagesRegistered) {
    return
  }

  HIGHLIGHT_LANGUAGES.forEach(([name, language]) => {
    if (!hljs.getLanguage(name)) {
      hljs.registerLanguage(name, language)
    }
  })

  highlightLanguagesRegistered = true
}

const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const normalizeLanguage = (language: string): string => {
  const normalized = language.trim().toLowerCase()
  return LANGUAGE_ALIASES[normalized] ?? normalized
}

const isRenderableHtmlFence = (info: string): boolean => {
  const hint = info.trim().split(/\s+/, 1)[0] ?? ''
  return RENDERABLE_HTML_LANGUAGES.has(hint.toLowerCase())
}

type MarkdownToken = ReturnType<MarkdownIt['parse']>[number]

const isClosedFence = (token: MarkdownToken, lines: string[]): boolean => {
  if (!token.map) {
    return false
  }

  let closingLine = lines[token.map[1] - 1] ?? ''
  while (/^[ \t]{0,3}>[ \t]?/.test(closingLine)) {
    closingLine = closingLine.replace(/^[ \t]{0,3}>[ \t]?/, '')
  }

  return closingLine.trimStart().startsWith(token.markup)
}

const hashText = (value: string): string => {
  let hash = 5381

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index)
  }

  return (hash >>> 0).toString(36)
}

const formatLanguageLabel = (language: string): string => {
  if (!language) {
    return 'Text'
  }

  if (LANGUAGE_LABELS[language]) {
    return LANGUAGE_LABELS[language]
  }

  const normalized = language.replace(/[-_]+/g, ' ').trim()
  if (!normalized) {
    return 'Text'
  }

  if (normalized.length <= 4) {
    return normalized.toUpperCase()
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

/** Return the presentation data used by CodeBlock without creating HTML markup. */
export const getCodeBlockPresentation = (
  code: string,
  languageInfo: string
): CodeBlockPresentation => {
  registerHighlightLanguages()

  const languageHint = languageInfo.trim().split(/\s+/, 1)[0] ?? ''
  const normalizedLanguage = normalizeLanguage(languageHint)
  const canHighlight = Boolean(normalizedLanguage && hljs.getLanguage(normalizedLanguage))
  const languageForClass = canHighlight ? normalizedLanguage : 'plaintext'
  const languageLabel = formatLanguageLabel(normalizedLanguage || languageForClass)

  let highlightedCode = escapeHtml(code)
  if (canHighlight) {
    highlightedCode = hljs.highlight(code, {
      language: normalizedLanguage,
      ignoreIllegals: true,
    }).value
  }

  return {
    languageClass: languageForClass,
    languageLabel,
    highlightedCode,
  }
}

/**
 * Get or create the markdown-it instance used by the token renderer. Fences remain ordinary
 * tokens; React owns their final presentation.
 */
const getMarkdownInstance = (options: MarkdownOptions = DEFAULT_OPTIONS): MarkdownIt => {
  if (!mdInstance) {
    mdInstance = new MarkdownIt({
      html: options.enableHtml,
      breaks: options.enableBreaks,
      linkify: options.enableLinkify,
      typographer: options.enableTypography,
    })

    if (options.enableTable) {
      mdInstance.enable(['table', 'strikethrough'])
    }
  }

  return mdInstance
}

const isSafeTokenUrl = (value: string): boolean => {
  const normalized = Array.from(value.trim())
    .filter((character) => {
      const codePoint = character.charCodeAt(0)
      return codePoint > 0x1f && codePoint !== 0x7f
    })
    .join('')
  if (/^(?:javascript|vbscript):/i.test(normalized)) {
    return false
  }
  if (/^data:/i.test(normalized)) {
    return /^data:image\/(?:gif|jpeg|jpg|png|webp);/i.test(normalized)
  }
  return true
}

const getTokenAttrs = (token: MarkdownToken, tag: string): Record<string, string> => {
  const attrs: Record<string, string> = {}

  const tokenAttrs = token.attrs ?? []
  tokenAttrs.forEach(([name, value]) => {
    if (!TOKEN_ATTRIBUTES.has(name)) {
      return
    }
    if ((name === 'href' || name === 'src') && !isSafeTokenUrl(value)) {
      return
    }
    attrs[name] = value
  })

  // MAX runs embedded in the host SPA: links must never navigate the shell away.
  if (tag === 'a') {
    attrs.target = attrs.target ?? '_blank'
    attrs.rel = 'noopener noreferrer'
  }

  return attrs
}

const getCurrentChildren = (stack: MarkdownElementNode[]): MarkdownRenderNode[] => {
  return stack[stack.length - 1]?.children ?? []
}

const appendText = (stack: MarkdownElementNode[], value: string): void => {
  if (value) {
    getCurrentChildren(stack).push({ kind: 'text', value })
  }
}

type TokenRenderContext = {
  lines: string[]
  config: MarkdownOptions
  htmlOrdinal: number
  codeOrdinal: number
}

const appendTokens = (
  tokens: MarkdownToken[],
  stack: MarkdownElementNode[],
  context: TokenRenderContext
): void => {
  for (const token of tokens) {
    if (token.type === 'fence') {
      const isHtmlLanguage = context.config.renderHtmlBlocks && isRenderableHtmlFence(token.info)
      const closed = isClosedFence(token, context.lines)
      const canRenderHtml = isHtmlLanguage && closed && Boolean(token.content.trim())

      if (canRenderHtml) {
        const ordinal = context.htmlOrdinal
        context.htmlOrdinal += 1
        getCurrentChildren(stack).push({
          kind: 'html-block',
          source: token.content,
          key: `html-${ordinal}-${hashText(token.content)}`,
        })
      } else if (isHtmlLanguage && !closed) {
        // Fence language matched, but the closing fence marker hasn't streamed in yet.
        // Show a pending placeholder instead of the raw growing source.
        const ordinal = context.htmlOrdinal
        context.htmlOrdinal += 1
        getCurrentChildren(stack).push({
          kind: 'html-block-pending',
          source: token.content,
          key: `html-pending-${ordinal}`,
        })
      } else {
        const ordinal = context.codeOrdinal
        context.codeOrdinal += 1
        getCurrentChildren(stack).push({
          kind: 'code',
          code: token.content,
          language: token.info,
          key: `code-${ordinal}-${hashText(`${token.info} ${token.content}`)}`,
        })
      }
      continue
    }

    if (token.type === 'code_block') {
      const ordinal = context.codeOrdinal
      context.codeOrdinal += 1
      getCurrentChildren(stack).push({
        kind: 'code',
        code: token.content,
        language: '',
        key: `code-${ordinal}-${hashText(token.content)}`,
      })
      continue
    }

    if (token.type === 'inline') {
      appendTokens(token.children ?? [], stack, context)
      continue
    }

    if (token.type === 'text' || token.type === 'entity') {
      appendText(stack, token.content)
      continue
    }

    if (token.type === 'code_inline') {
      getCurrentChildren(stack).push({
        kind: 'element',
        tag: 'code',
        attrs: { class: INLINE_CODE_CLASS },
        children: [{ kind: 'text', value: token.content }],
      })
      continue
    }

    if (token.type === 'softbreak') {
      if (context.config.enableBreaks) {
        getCurrentChildren(stack).push({ kind: 'element', tag: 'br', attrs: {}, children: [] })
      } else {
        appendText(stack, '\n')
      }
      continue
    }

    if (token.type === 'hardbreak') {
      getCurrentChildren(stack).push({ kind: 'element', tag: 'br', attrs: {}, children: [] })
      continue
    }

    if (token.type === 'image') {
      getCurrentChildren(stack).push({
        kind: 'element',
        tag: 'img',
        attrs: getTokenAttrs(token, 'img'),
        children: [],
      })
      continue
    }

    if (token.type === 'html_inline' || token.type === 'html_block') {
      appendText(stack, token.content)
      continue
    }

    if (token.type === 'hr') {
      getCurrentChildren(stack).push({
        kind: 'element',
        tag: 'hr',
        attrs: getTokenAttrs(token, 'hr'),
        children: [],
      })
      continue
    }

    // markdown-it hides the paragraph wrappers of tight list items; their inline content
    // belongs directly to the <li>.
    if (token.hidden) {
      continue
    }

    if (token.nesting === 1) {
      const tag = token.tag === 's' ? 'del' : token.tag
      const node: MarkdownElementNode = {
        kind: 'element',
        tag,
        attrs: getTokenAttrs(token, tag),
        children: [],
      }
      getCurrentChildren(stack).push(node)
      stack.push(node)
      continue
    }

    if (token.nesting === -1) {
      if (stack.length > 1) {
        stack.pop()
      }
      continue
    }

    if (token.content) {
      appendText(stack, token.content)
    }
  }
}

/**
 * Parse markdown tokens into safe descriptors that can be rendered as React elements.
 * HTML fences are preserved as nodes at their actual nesting depth.
 */
export const parseMarkdownNodes = (
  markdownInput: string,
  options: Partial<MarkdownOptions> = {}
): MarkdownRenderNode[] => {
  if (!markdownInput || typeof markdownInput !== 'string') {
    return []
  }

  const config = { ...DEFAULT_OPTIONS, ...options }

  try {
    const md = getMarkdownInstance(config)
    const root: MarkdownElementNode = {
      kind: 'element',
      tag: 'fragment',
      attrs: {},
      children: [],
    }
    const stack = [root]
    appendTokens(md.parse(markdownInput, {}), stack, {
      lines: markdownInput.split(/\r?\n/),
      config,
      htmlOrdinal: 0,
      codeOrdinal: 0,
    })
    return root.children
  } catch (error) {
    maxDebug.error('Markdown parsing failed', {
      errorMessage: error instanceof Error ? error.message : String(error),
    })
    return []
  }
}
